import { useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { isoDateTime } from '@fleet/shared';
import { useCreateEnquiryMutation, useEnquiriesQuery } from '@/api/endpoints';
import type { Enquiry } from '@/api/types';
import { Button } from '@/design/Button';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import {
  DateTimeInput,
  Field,
  FieldRow,
  PhoneInput,
  SelectInput,
  TextInput,
} from '@/design/Field';
import { Modal } from '@/design/Modal';
import { Icon } from '@/design/icons';
import { Panel } from '@/design/Panel';
import { StatusPill } from '@/design/StatusPill';
import { LostEnquiryModal } from './LostEnquiryModal';
import { readApiError } from '@/lib/apiError';
import { fromInputValue } from '@/lib/time';
import { checkName, checkPhone, checkWindow } from '@/lib/validate';
import { useTime } from '@/lib/time';
import { ENQUIRY_SOURCES, enquiryStatus } from './status';

/**
 * Enquiries.
 *
 * The form is at the top of the list, open, with two required fields. A staff
 * member with a customer talking at them will not navigate to another screen
 * and fill in eight boxes — and an enquiry that never gets written down is
 * exactly what we are replacing.
 */
/**
 * Which enquiries are on screen.
 *
 * "Show all" used to be a single toggle, which is how QA came to ask where a
 * lost enquiry goes: the answer was "into the same undifferentiated list as
 * everything else, if you happen to turn the switch on". Naming the four
 * states answers the question by existing — Lost is a tab you can press, and
 * the reason somebody typed is on the row when you get there.
 */
const VIEWS = [
  { value: 'open', label: 'Waiting', status: 'open' },
  { value: 'won', label: 'Booked', status: 'won' },
  { value: 'lost', label: 'Lost', status: 'lost' },
  { value: 'all', label: 'All', status: '' },
] as const;

type View = (typeof VIEWS)[number]['value'];

export function EnquiriesPage(): ReactElement {
  const [view, setView] = useState<View>('open');
  const [adding, setAdding] = useState(false);
  const [losing, setLosing] = useState<Enquiry | null>(null);
  const status = VIEWS.find((option) => option.value === view)?.status ?? 'open';
  const { data, isLoading, isError, refetch } = useEnquiriesQuery(
    status === '' ? {} : { status },
  );
  const [createEnquiry, creating] = useCreateEnquiryMutation();
  const navigate = useNavigate();
  const { formatDateTime } = useTime();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState<string>('phone');
  const [wanted, setWanted] = useState('');
  const [route, setRoute] = useState('');
  // The dates they asked about. Already on the enquiry record and already
  // read by the booking form when converting — there was simply nowhere to
  // type them, so every conversion started by asking the customer again for
  // something they had said on the first call.
  const [wantedFrom, setWantedFrom] = useState('');
  const [wantedTo, setWantedTo] = useState('');

  const problems = readApiError(creating.error);

  /** Checked as it is typed. The server still decides; this is just faster. */
  const nameProblem = name === '' ? undefined : checkName(name);
  const phoneProblem = phone === '' ? undefined : checkPhone(phone);
  const readyToSave =
    name !== '' &&
    phone !== '' &&
    nameProblem === undefined &&
    phoneProblem === undefined &&
    checkWindow(wantedFrom, wantedTo) === undefined;

  async function add(): Promise<void> {
    try {
      await createEnquiry({
        name,
        phone,
        source: source as Enquiry['source'],
        vehicle_wanted: wanted,
        route,
        // Left off entirely when blank: an enquiry is often a name and a
        // number and nothing else, and that has to stay two fields' work.
        ...(wantedFrom === '' ? {} : { wanted_from: fromInputValue(wantedFrom) }),
        ...(wantedTo === '' ? {} : { wanted_to: fromInputValue(wantedTo) }),
      }).unwrap();
    } catch {
      // Rendered under the fields it belongs to. The modal stays open with
      // what was typed still in it, because retyping a call you have just
      // taken is not something anybody will do.
      return;
    }
    setName('');
    setPhone('');
    setWanted('');
    setRoute('');
    setWantedFrom('');
    setWantedTo('');
    setAdding(false);
  }

  const columns: readonly Column<Enquiry>[] = [
    { key: 'name', header: 'Name', render: (row) => row.name },
    {
      key: 'phone',
      header: 'Phone',
      render: (row) => (
        <a href={`tel:${row.phone}`} className="identifier hover:underline">
          {row.phone}
        </a>
      ),
    },
    { key: 'wanted', header: 'Wanted', secondary: true, render: (row) => row.vehicle_wanted || '—' },
    { key: 'route', header: 'Route', secondary: true, render: (row) => row.route || '—' },
    {
      key: 'when',
      header: 'Taken',
      secondary: true,
      render: (row) => (
        <span className="tnum">{formatDateTime(isoDateTime(row.created_at))}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusPill status={enquiryStatus(row.status)} />
      ),
    },
    {
      key: 'actions',
      header: 'Next',
      render: (row) =>
        row.status === 'won' || row.status === 'lost' ? (
          <span className="text-small text-ink-muted">
            {row.status === 'won'
              ? (row.booking_reference ?? 'Booked')
              : `Lost — ${row.lost_reason || 'no reason recorded'}`}
          </span>
        ) : (
          <div className="flex gap-2">
            <Button
              intent="primary"
              onClick={() => navigate(`/bookings/new?enquiry=${row.id}`)}
            >
              Make a booking
            </Button>
            <Button intent="quiet" onClick={() => setLosing(row)}>
              Lost
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-display font-semibold">Enquiries</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button intent="primary" size="touch" onClick={() => setAdding(true)}>
            <Icon name="plus" width="16" height="16" />
            Add enquiry
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {VIEWS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setView(option.value)}
              className={[
                'rounded-sm px-2.5 py-1 text-base',
                view === option.value
                  ? 'bg-signal-wash font-semibold text-signal'
                  : 'text-ink-muted hover:bg-paper hover:text-ink',
              ].join(' ')}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <Panel
        title={
          view === 'open'
            ? 'Waiting for an answer'
            : view === 'won'
              ? 'Turned into bookings'
              : view === 'lost'
                ? 'Lost, and why'
                : 'Every enquiry'
        }
        note={
          data === undefined
            ? 'Every call, message and walk-in'
            : `${String(data.count)} ${data.count === 1 ? 'enquiry' : 'enquiries'}`
        }
      >
        <DataTable
          columns={columns}
          rows={data?.results ?? []}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          errorHeadline="The enquiries did not load"
          errorDetail="The server did not answer. Check the connection and load them again."
          empty={{
            headline:
              view === 'lost'
                ? 'Nothing has been marked lost'
                : view === 'won'
                  ? 'No enquiry has become a booking yet'
                  : view === 'all'
                    ? 'No enquiries yet'
                    : 'Nothing is waiting',
            detail:
              'Add the enquiry while the customer is still on the phone. It stays on the waiting list until someone books it or marks it lost — and a lost one moves to the Lost tab with the reason on it, rather than disappearing.',
          }}
        />
      </Panel>

      {/*
        A modal, because this is five fields and the list behind it is what
        somebody wants to be looking at again the moment it closes. It used to
        sit open above the table permanently, which pushed the enquiries —
        the actual content of the screen — below the fold on every visit.
      */}
      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add an enquiry"
        note="A name and a number are enough. Everything else can follow."
        size="wide"
        footer={
          <>
            <Button
              intent="primary"
              size="touch"
              form="add-enquiry"
              type="submit"
              disabled={creating.isLoading || !readyToSave}
            >
              <Icon name="plus" width="16" height="16" />
              {creating.isLoading ? 'Saving' : 'Save enquiry'}
            </Button>
            <Button size="touch" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </>
        }
      >
        <form
          id="add-enquiry"
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
        >
          <FieldRow>
            <Field label="Name" required error={nameProblem ?? problems.fields['name']}>
              {({ id, describedBy }) => (
                <TextInput id={id} describedBy={describedBy} value={name} onChange={setName} />
              )}
            </Field>
            <Field
              label="Phone number"
              required
              error={phoneProblem ?? problems.fields['phone']}
            >
              {({ id, describedBy }) => (
                <PhoneInput
                  id={id}
                  describedBy={describedBy}
                  value={phone}
                  onChange={setPhone}
                />
              )}
            </Field>
          </FieldRow>

          {/* When they want it. Carried straight onto the booking form when
              this enquiry is converted, so the dates are typed once. Both are
              optional — a call that is only a name and a number is still worth
              writing down, and that is the whole point of this screen. */}
          <FieldRow>
            <Field label="Out" error={problems.fields['wanted_from']}>
              {({ id, describedBy }) => (
                <DateTimeInput
                  id={id}
                  describedBy={describedBy}
                  value={wantedFrom}
                  onChange={setWantedFrom}
                />
              )}
            </Field>
            <Field
              label="Back"
              error={checkWindow(wantedFrom, wantedTo) ?? problems.fields['wanted_to']}
            >
              {({ id, describedBy }) => (
                <DateTimeInput
                  id={id}
                  describedBy={describedBy}
                  value={wantedTo}
                  onChange={setWantedTo}
                />
              )}
            </Field>
          </FieldRow>

          <FieldRow>
            <Field label="What they want">
              {({ id, describedBy }) => (
                <TextInput id={id} describedBy={describedBy} value={wanted} onChange={setWanted} />
              )}
            </Field>
            <Field label="Where to">
              {({ id, describedBy }) => (
                <TextInput id={id} describedBy={describedBy} value={route} onChange={setRoute} />
              )}
            </Field>
          </FieldRow>

          <Field label="How they got in touch">
            {({ id, describedBy }) => (
              <SelectInput
                id={id}
                describedBy={describedBy}
                value={source}
                onChange={setSource}
                options={[...ENQUIRY_SOURCES]}
              />
            )}
          </Field>

          {problems.detail !== undefined && (
            <p className="font-medium text-alert" role="alert">
              {problems.detail}
            </p>
          )}
        </form>
      </Modal>

      <LostEnquiryModal enquiry={losing} onClose={() => setLosing(null)} />
    </div>
  );
}
